import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, TreeDeciduous } from 'lucide-react';

export default function NVCTreeOfLife() {
  const navigate = useNavigate();
  const [selectedFocus, setSelectedFocus] = useState(null);

  const focusOptions = [
    {
      id: 'self-empathy',
      title: 'Self-Empathy',
      subtitle: '(Self-Connection)',
      description: 'Opening my heart to...',
      branches: [
        {
          title: '...what\'s alive in you:',
          content: [
            '(Judgments?)',
            'Could s/he be feeling...?',
            'Could s/he be needing...?'
          ]
        },
        {
          title: '...what\'s alive in me:',
          content: [
            '(Judgments?)',
            'How am I feeling?',
            'What am I needing?'
          ]
        }
      ],
      color: 'from-green-900/30 to-emerald-900/30',
      borderColor: 'border-green-700/50'
    },
    {
      id: 'empathy',
      title: 'Empathy',
      subtitle: 'Connecting with what\'s alive in you',
      description: '',
      branches: [
        {
          title: 'The Four Components:',
          content: [
            '(When you see/hear...)',
            'Are you feeling...',
            'because you need...?',
            '(Would you like...?)'
          ]
        }
      ],
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/50'
    },
    {
      id: 'self-expression',
      title: 'Self-Expression',
      subtitle: 'Communicating what\'s alive in me',
      description: '',
      branches: [
        {
          title: 'The Four Components:',
          content: [
            'When I see/hear...',
            'I feel...',
            'because I need....',
            'Would you be willing to...?'
          ]
        }
      ],
      color: 'from-purple-900/30 to-pink-900/30',
      borderColor: 'border-purple-700/50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">The NVC Tree of Life</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 10</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-12">
            <TreeDeciduous className="h-20 w-20 text-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">Three Focus Options for Connection</h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Like a tree with three main branches, NVC offers us three ways to create connection. 
              Each branch leads to the same roots: feelings and needs.
            </p>
          </div>

          {/* Original Diagram */}
          <div className="bg-white rounded-lg p-6 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_belonging-sessions-1/artifacts/62rxcjrj_TREE%20LIFE.jpg"
              alt="The NVC Tree of Life - Three Focus Options"
              className="w-full h-auto rounded"
              style={{ imageRendering: 'high-quality', maxWidth: '100%' }}
            />
          </div>

          {/* Three Focus Options */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {focusOptions.map((option, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedFocus(option.id)}
                className={`bg-gradient-to-br ${option.color} border ${option.borderColor} rounded-lg p-6 text-left hover:scale-[1.02] transition-all ${
                  selectedFocus === option.id ? 'ring-2 ring-white' : ''
                }`}
                data-testid={`focus-option-${idx}`}
              >
                <h3 className="text-2xl font-bold text-white mb-2">{option.title}</h3>
                <p className="text-slate-300 text-sm mb-4">{option.subtitle}</p>
                {option.description && (
                  <p className="text-slate-200 italic mb-4">{option.description}</p>
                )}
                {option.branches.map((branch, branchIdx) => (
                  <div key={branchIdx} className="mt-4">
                    <p className="text-white font-semibold mb-2">{branch.title}</p>
                    <ul className="space-y-1">
                      {branch.content.map((item, itemIdx) => (
                        <li key={itemIdx} className="text-slate-200 text-sm pl-4 border-l-2 border-white/30">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </button>
            ))}
          </div>

          {/* Decision Point */}
          <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-700/30 rounded-lg p-8 text-center mb-12">
            <h3 className="text-2xl font-bold text-white mb-4">Which option do I want to choose next?</h3>
            <p className="text-slate-200 text-lg">
              Empathy? Self-Expression?
            </p>
          </div>

          {/* Bottom Insight */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-8 text-center">
            <p className="text-xl text-slate-200 leading-relaxed">
              <span className="font-semibold text-white">New strategies arise from connection,</span><br />
              sometimes without words
            </p>
            <p className="text-slate-400 text-sm mt-4">
              © 2008 by Inbal Kashtan • nvc@baynvc.org • www.cnvc.org • www.baynvc.org
            </p>
          </div>

          {/* Teaching Notes */}
          <div className="mt-12 bg-blue-900/20 border border-blue-700/30 rounded-lg p-8">
            <h3 className="text-xl font-bold text-white mb-4">Teaching Notes</h3>
            <div className="space-y-3 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold">Self-Empathy</span> is where we start—connecting with our own feelings and needs, 
                and guessing at others' feelings and needs without expressing them yet.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">Empathy</span> is when we verbally connect with what's alive in the other person, 
                helping them feel heard and understood.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">Self-Expression</span> is when we share what's alive in us, 
                revealing our feelings and needs to create mutual understanding.
              </p>
              <p className="leading-relaxed mt-4 text-slate-300 italic">
                The magic happens when we move fluidly between these three options, 
                always staying connected to the feelings and needs—the roots of the tree.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
